import React from 'react';

import type { ManagedHost } from '../types';

export type MaintenanceInventoryView = 'current' | 'history' | 'unknown' | 'all';

type HostObservation = Pick<ManagedHost, 'online_executor_count' | 'executor_count' | 'hostname_ambiguous'>;

// These are observations, not physical-host reconciliation. The API's host ID
// remains authoritative; names and references are never used to merge rows.
export function maintenanceInventoryGroup(host: HostObservation): Exclude<MaintenanceInventoryView, 'all'> {
  if (host.hostname_ambiguous) return 'unknown';
  if (host.online_executor_count > 0) return 'current';
  return host.executor_count > 0 ? 'history' : 'unknown';
}

export function maintenanceInventoryLabel(host: HostObservation): string {
  switch (maintenanceInventoryGroup(host)) {
    case 'current': return 'Current · online executor';
    case 'history': return 'Historical / offline registration';
    case 'unknown': return host.hostname_ambiguous ? 'Unknown · ambiguous identity' : 'Unknown · no executor';
  }
}

export function maintenanceSelectableHostIds(
  hosts: ManagedHost[],
  view: MaintenanceInventoryView,
  reservedHostIds: ReadonlySet<string>
): string[] {
  return hosts
    .filter((host) => (view === 'all' || maintenanceInventoryGroup(host) === view)
      && host.ready && !reservedHostIds.has(host.id))
    .map((host) => host.id);
}

export default function HostMaintenanceInventory({ hosts, view, onChange, canViewNodes, canOperate }: {
  hosts: ManagedHost[];
  canViewNodes: boolean;
  canOperate: boolean;
  view: MaintenanceInventoryView;
  onChange: (view: MaintenanceInventoryView) => void;
}) {
  const count = (group: MaintenanceInventoryView) => hosts.filter((host) =>
    group === 'all' || maintenanceInventoryGroup(host) === group
  ).length;

  return (
    <div className="stack stack--compact">
      <nav className="row row--compact" aria-label="Host and executor inventory">
        <a href="#host-maintenance">Host maintenance</a>
        {canViewNodes && <a href="#executor-nodes">Executor nodes</a>}
        {canOperate && <a href="#executor-tokens">Enrollment tokens</a>}
      </nav>
      <label>
        Inventory view{' '}
        <select value={view} onChange={(event) => onChange(event.target.value as MaintenanceInventoryView)}>
          <option value="current">Current hosts ({count('current')})</option>
          <option value="history">Historical / offline registrations ({count('history')})</option>
          <option value="unknown">Unknown / ambiguous registrations ({count('unknown')})</option>
          <option value="all">All registrations ({count('all')})</option>
        </select>
      </label>
      <p className="hint">
        Rows use canonical managed-host IDs, each bound to an enrollment token. Executor instances are
        counted within that host, not merged by hostname. Current means an enabled executor reported
        online recently, not that maintenance is ready. Historical/offline does not prove retirement;
        unknown means no executor is registered or the identity is ambiguous. No registrations are deleted.
      </p>
    </div>
  );
}
